import React, { useState, useEffect, useMemo } from 'react';
import { ModalX } from '../../../components';
import {
    Button,
    Checkbox,
    Input,
    List,
    ListItem,
    Spinner,
    Textarea,
    Tab,
    TabsHeader,
    TabsBody,
    TabPanel,
    Tabs
} from '@material-tailwind/react';
import { ExternalLinkIcon, ScanQrCodeIcon, SearchIcon, SofaIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { dateToTime, getOrdinal, NotifyError, timeToDate } from '../../../utils/functions';
import { useGetOffices } from '../../../custom-hooks/use-offices';
import { useGetSeats } from '../../../custom-hooks/use-seats';
import { useAddBooking, useUpdateBooking } from '../../../custom-hooks/use-bookings';
import { GetBookingsBySeat } from '../../../services/backend';
import moment from 'moment';
import { InterpretActiveStatus } from '../../../utils/ui-interpreters';
import { useConfirm } from '../../../custom-hooks/confirm-dialog';
import { OPERATIONAL_STATUSES, VERIFICATION_TYPES } from '../../../utils/enums';

const ACTIVE_BOOKING_STATUSES = [
    VERIFICATION_TYPES.APPROVED,
    VERIFICATION_TYPES.PENDING,
    VERIFICATION_TYPES.NOTIFIED,
];

const isBookingFullTime = (booking) => !(booking?.dailyStartTime && booking?.dailyEndTime);

const getBookingUserName = (booking) => {
    if (typeof booking?.user === 'object' && booking?.user?.name) {
        return booking.user.name;
    }
    if (booking?.contract?.user?.name) {
        return booking.contract.user.name;
    }
    return 'Deleted User';
};

const getUsersListText = (bookings = []) => {
    const users = [...new Set(bookings.map((booking) => getBookingUserName(booking)).filter(Boolean))];
    return users.length > 0 ? users.join(', ') : 'Deleted User';
};

const BookingFormModal = ({
    isOpen,
    onClose,
    // isUpdating,
    updatingBookingId,
    customerId,
    defaultData,
    onSuccess
}) => {

    const isUpdating = !!updatingBookingId;
    const [formData, setFormData] = useState({
        office: null,
        seat: null,
        contractEndDate: null,
        startDate: '',
        endDate: '',
        dailyStartTime: '',
        dailyEndTime: '',
        rentTotal: 0,
        firstMonthRent: '',
        advanceAmount: 0,
        securityAmount: 0,
        comment: '',
        ...defaultData
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [showAvailableOnly, setShowAvailableOnly] = useState(true);
    const [showAvailableOfficesOnly, setShowAvailableOfficesOnly] = useState(true);
    const [activeTab, setActiveTab] = useState('');
    const [isFullDayAccess, setIsFullDayAccess] = useState(false);
    const [isOpenEnded, setIsOpenEnded] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    // Once the admin has typed into First Month Rent directly (or it was loaded with an
    // existing saved value), the auto-suggest effect stops touching it — it only keeps
    // live-updating the suggestion while the field is still fully automatic.
    const [firstMonthRentTouched, setFirstMonthRentTouched] = useState(false);

    const { data: offices, isLoading: isLoadingOffices, refetch: refetchOffices } = useGetOffices();
    const { data: seats, isLoading: isLoadingSeats, refetch: refetchSeats } = useGetSeats();

    const updateBookingMutation = useUpdateBooking();
    const addBookingMutation = useAddBooking();

    const { confirm, ConfirmationDialog } = useConfirm();

    // Force a fresh refetch of seats and offices whenever the modal opens,
    // so that embedded seat.bookings / office.bookings arrays are up to date.
    useEffect(() => {
        if (isOpen) {
            refetchSeats();
            refetchOffices();
        }
    }, [isOpen, refetchSeats, refetchOffices]);

    // Set initial data when booking data is loaded
    useEffect(() => {
        if (defaultData) {
            setFormData({
                contract: defaultData.contract || null,
                user: defaultData.user || null,
                office: defaultData.office?._id || null,
                seat: defaultData.seat?._id || null,
                contractEndDate: defaultData.contractEndDate || defaultData.contract?.endDate || null,
                startDate: moment(defaultData.startDate).format('YYYY-MM-DD') || '',
                endDate: moment(defaultData.endDate).format('YYYY-MM-DD') || '',
                dailyStartTime: defaultData.dailyStartTime ? dateToTime(defaultData.dailyStartTime) : '',
                dailyEndTime: defaultData.dailyEndTime ? dateToTime(defaultData.dailyEndTime) : '',
                rentTotal: defaultData.rentTotal || 0,
                firstMonthRent: defaultData.firstMonthRent ?? '',
                advanceAmount: defaultData.advanceAmount || 0,
                securityAmount: defaultData.securityAmount || 0,
                comment: defaultData.comment || '',
                status: defaultData.status || '',
            });
            setFirstMonthRentTouched(defaultData.firstMonthRent != null);
            setIsOpenEnded(defaultData.endDate === null);
            setIsFullDayAccess(defaultData.dailyStartTime === null && defaultData.dailyEndTime === null);

            // Set active tab based on what the booking contains
            if (defaultData.office) {
                setActiveTab('office');
            } else if (defaultData.seat) {
                setActiveTab('seat');
            }
        }
    }, [defaultData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Clears the first-month-rent field (and releases manual control) when the booking
    // starts on the 1st — no proration needed there, and the field is hidden. Does NOT
    // auto-fill a suggestion as Total Rent/Start Date are typed — that only happens when
    // the admin focuses the (still-empty, untouched) field, see handleFirstMonthRentFocus
    // below. Avoids the common case — first month IS the full rent, not a prorated one —
    // silently pre-filling a value the admin then has to notice and clear themselves.
    useEffect(() => {
        if (!formData.startDate) return;
        const startMoment = moment(formData.startDate);

        if (startMoment.date() === 1) {
            setFirstMonthRentTouched(false);
            setFormData(prev => (prev.firstMonthRent === '' ? prev : { ...prev, firstMonthRent: '' }));
        }
    }, [formData.startDate]);

    const handleFirstMonthRentChange = (e) => {
        setFirstMonthRentTouched(true);
        handleInputChange(e);
    };

    // Fills First Month Rent with a suggested value the first time the admin focuses the
    // still-empty, untouched field. Fixed 30-day-month baseline on purpose, regardless of
    // the booking's actual calendar month — a simple, predictable "days remaining out of
    // 30" estimate rather than a calendar-accurate proration.
    const handleFirstMonthRentFocus = () => {
        if (firstMonthRentTouched) return;
        if (formData.firstMonthRent !== '' && formData.firstMonthRent != null) return;
        if (!formData.startDate) return;
        const startMoment = moment(formData.startDate);
        if (startMoment.date() === 1) return;
        const rent = Number(formData.rentTotal) || 0;
        if (rent <= 0) return;
        const daysRemaining = Math.max(0, 30 - startMoment.date() + 1);
        const suggested = Math.round((rent * daysRemaining) / 30);
        setFirstMonthRentTouched(true);
        setFormData(prev => ({ ...prev, firstMonthRent: suggested }));
    };

    const showFirstMonthRent = !!formData.startDate && moment(formData.startDate).date() !== 1;

    const handleTimeChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleOfficeSelection = (officeId) => {
        setFormData(prev => ({
            ...prev,
            office: officeId
        }));
    };

    const handleSeatSelection = (seatId) => {
        setFormData(prev => ({
            ...prev,
            seat: seatId
        }));
    };

    const isDateRangeOverlapping = (existingStartDate, existingEndDate, newStartDate, newEndDate) => {
        if (!existingStartDate || !newStartDate) {
            return false;
        }

        const existingStart = moment(existingStartDate).startOf('day');
        const existingEnd = existingEndDate ? moment(existingEndDate).endOf('day') : null;
        const nextStart = moment(newStartDate).startOf('day');
        const nextEnd = newEndDate ? moment(newEndDate).endOf('day') : null;

        const existingStartsBeforeNewEnds = !nextEnd || existingStart.isSameOrBefore(nextEnd);
        const newStartsBeforeExistingEnds = !existingEnd || nextStart.isSameOrBefore(existingEnd);

        return existingStartsBeforeNewEnds && newStartsBeforeExistingEnds;
    };

    const validateSeatBookingConflict = async ({ seatId, startDate, endDate, dailyStartTime, dailyEndTime }) => {
        if (!seatId) {
            return;
        }

        const selectedSeat = seats?.find((seat) => seat?._id === seatId);

        let seatBookings = [];
        try {
            const bookingsResponse = await GetBookingsBySeat(seatId);
            seatBookings = bookingsResponse?.data?.bookings || [];
        } catch (error) {
            seatBookings = Array.isArray(selectedSeat?.bookings) ? selectedSeat.bookings : [];
            console.error('Failed to fetch seat bookings for conflict validation', error);
        }

        if (!selectedSeat && !seatBookings.length) {
            return;
        }

        const overlappingBookings = (seatBookings || []).filter((existingBooking) => {
            const isOwnBookingDuringUpdate = isUpdating && existingBooking?._id === updatingBookingId;
            const isStatusAllowed = !existingBooking?.status || ACTIVE_BOOKING_STATUSES.includes(existingBooking?.status);

            if (isOwnBookingDuringUpdate || !isStatusAllowed) {
                return false;
            }

            const isOverlapping = isDateRangeOverlapping(existingBooking.startDate, existingBooking.endDate, startDate, endDate);

            if (!isOverlapping) {
                return false;
            }

            const freezeData = existingBooking.freeze?.isFrozen ? existingBooking.freeze : (existingBooking.contract?.freeze?.isFrozen ? existingBooking.contract.freeze : null);

            if (freezeData && freezeData.freezeStartDate && freezeData.freezeEndDate) {
                const freezeStart = moment(freezeData.freezeStartDate).startOf('day');
                const freezeEnd = moment(freezeData.freezeEndDate).endOf('day');

                const overlapsBeforeFreeze = isDateRangeOverlapping(
                    existingBooking.startDate,
                    freezeStart.clone().subtract(1, 'days'),
                    startDate,
                    endDate
                );

                const overlapsAfterFreeze = isDateRangeOverlapping(
                    freezeEnd.clone().add(1, 'days'),
                    existingBooking.endDate,
                    startDate,
                    endDate
                );

                return overlapsBeforeFreeze || overlapsAfterFreeze;
            }

            return true;
        });

        if (overlappingBookings.length === 0) {
            return;
        }

        const fullTimeOverlappingBookings = overlappingBookings.filter((booking) => isBookingFullTime(booking));
        const partTimeOverlappingBookings = overlappingBookings.filter((booking) => !isBookingFullTime(booking));
        const isNewBookingFullTime = isBookingFullTime({ dailyStartTime, dailyEndTime });
        const seatDisplayName = `Seat # ${selectedSeat?.seatCode || seatId || 'N/A'}`;

        if (fullTimeOverlappingBookings.length > 0) {
            throw new Error(
                `${seatDisplayName} is already booked full time for the following user(s): ${getUsersListText(fullTimeOverlappingBookings)}.`
            );
        }

        if (isNewBookingFullTime && partTimeOverlappingBookings.length > 0) {
            throw new Error(
                `${seatDisplayName} is booked for part time for the following user(s): ${getUsersListText(partTimeOverlappingBookings)} and can be booked part time only.`
            );
        }

        // Part-time vs Part-time: check for time overlap
        if (!isNewBookingFullTime && partTimeOverlappingBookings.length > 0) {
            const newStartMin = toMinutes(dailyStartTime);
            const newEndMin = toMinutes(dailyEndTime);

            const timeConflicts = partTimeOverlappingBookings.filter((existing) => {
                const existStartMin = toMinutes(moment(existing.dailyStartTime).format('HH:mm'));
                const existEndMin = toMinutes(moment(existing.dailyEndTime).format('HH:mm'));
                return timesOverlap(newStartMin, newEndMin, existStartMin, existEndMin);
            });

            if (timeConflicts.length > 0) {
                throw new Error(
                    `${seatDisplayName} has overlapping part-time bookings for the selected time slot. Booked by: ${getUsersListText(timeConflicts)}.`
                );
            }
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setHasSubmitted(true);

        if (!isOpenEnded && formData.startDate && formData.endDate && moment(formData.startDate).isAfter(moment(formData.endDate), 'day')) {
            NotifyError('Booking start date cannot be greater than end date', 'invalid booking date range');
            return;
        }

        if (!isOpenEnded && formData.contractEndDate && formData.endDate && moment(formData.endDate).isAfter(moment(formData.contractEndDate), 'day')) {
            NotifyError('Booking end date cannot be greater than the contract end date', 'booking out of contract range');
            return;
        }

        // If part-time access is selected, both times are required
        if (!isFullDayAccess && formData.seat && (!formData.dailyStartTime || !formData.dailyEndTime)) {
            NotifyError('Please set both Daily Start Time and Daily End Time for part-time seat access', 'missing time');
            return;
        }

        // Rent must be at least 1000
        if (!formData.rentTotal || Number(formData.rentTotal) < 1000) {
            NotifyError('Total Rent must be at least 1,000', 'invalid rent');
            return;
        }

        try {
            await validateSeatBookingConflict({
                seatId: formData.seat,
                startDate: formData.startDate,
                endDate: isOpenEnded ? null : formData.endDate,
                dailyStartTime: isFullDayAccess ? null : formData.dailyStartTime,
                dailyEndTime: isFullDayAccess ? null : formData.dailyEndTime,
            });
        } catch (error) {
            NotifyError(error?.message || 'Seat booking conflict', 'seat booking conflict');
            return;
        }

        if (isUpdating) {
            try {
                const today = moment().startOf('day');
                const endDateMoment = !isOpenEnded && formData.endDate ? moment(formData.endDate).startOf('day') : null;
                const startDateMoment = formData.startDate ? moment(formData.startDate).startOf('day') : null;

                if (endDateMoment && endDateMoment.isSameOrBefore(today) && formData.status !== VERIFICATION_TYPES.COMPLETED) {
                    const confirmation = await confirm({
                        title: 'Complete Booking',
                        message: 'The end date is in the past or today. This booking will be marked as completed immediately. Do you want to proceed?',
                        confirmText: 'Yes, Complete',
                        cancelText: 'Cancel',
                        confirmBtnColor: 'green'
                    });
                    if (!confirmation) {
                        return;
                    }
                } else if (startDateMoment && startDateMoment.isAfter(today) && formData.status !== VERIFICATION_TYPES.PENDING) {
                    const confirmation = await confirm({
                        title: 'Mark Booking as Pending',
                        message: 'The start date is in the future. This booking will be marked as pending until the start date. Do you want to proceed?',
                        confirmText: 'Yes, Proceed',
                        cancelText: 'Cancel',
                        confirmBtnColor: 'blue'
                    });
                    if (!confirmation) {
                        return;
                    }
                }

                const updateData = {
                    user: customerId,
                    contract: formData.contract,
                    startDate: new Date(formData.startDate),
                    endDate: isOpenEnded ? null : new Date(formData.endDate),
                    dailyStartTime: isFullDayAccess ? null : timeToDate(formData.dailyStartTime),
                    dailyEndTime: isFullDayAccess ? null : timeToDate(formData.dailyEndTime),
                    rentTotal: formData.rentTotal,
                    firstMonthRent: formData.firstMonthRent === '' ? null : Number(formData.firstMonthRent),
                    advanceAmount: Number(formData.advanceAmount || 0),
                    securityAmount: Number(formData.securityAmount || 0),
                    comment: formData.comment,
                    // Don't update these fields as per requirements
                    ...(formData.office && { office: formData.office }),
                    ...(formData.seat && { seat: formData.seat })
                };

                await updateBookingMutation.mutateAsync({
                    bookingId: updatingBookingId,
                    data: updateData
                });
                onSuccess();
                onClose();
            } catch (error) {
                console.error('Error updating booking', error);
            } finally {
                refetchOffices();
                refetchSeats();
            }
        }
        else {
            if (formData.office && formData.seat) {
                NotifyError('Please select either office or seat, not both', 'office and seat both selected');
                return;
            }
            try {
                const newBooking = {
                    user: customerId,
                    contract: formData.contract,
                    ...(formData.office && { office: formData.office }),
                    ...(formData.seat && { seat: formData.seat }),
                    startDate: new Date(formData.startDate),
                    endDate: isOpenEnded ? null : new Date(formData.endDate),
                    dailyStartTime: isFullDayAccess ? null : timeToDate(formData.dailyStartTime),
                    dailyEndTime: isFullDayAccess ? null : timeToDate(formData.dailyEndTime),
                    rentTotal: formData.rentTotal || 0,
                    firstMonthRent: formData.firstMonthRent === '' ? null : Number(formData.firstMonthRent),
                    advanceAmount: Number(formData.advanceAmount || 0),
                    securityAmount: Number(formData.securityAmount || 0),
                    comment: formData.comment || '',
                }
                await addBookingMutation.mutateAsync(newBooking);
                onSuccess();
                onClose();
            } catch (error) {
                console.error('Error adding booking', error);
            }
        }
    };

    const filteredOffices = React.useMemo(() => offices?.filter(office =>
        office.code.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [], [offices, searchTerm]);

    const filteredSeats = React.useMemo(() => seats?.filter(seat =>
        seat.seatCode.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [], [seats, searchTerm]);

    // Helper: returns true if a booking is active and NOT frozen (i.e. it actually occupies the space)
    const isActiveNonFrozenBooking = React.useCallback((booking) => {
        if (isUpdating && booking._id === updatingBookingId) return false;
        const isStatusAllowed = !booking?.status || ACTIVE_BOOKING_STATUSES.includes(booking?.status);
        if (!isStatusAllowed) return false;
        // Frozen bookings don't block the space
        const isFrozen = booking.freeze?.isFrozen;
        return !isFrozen;
    }, [isUpdating, updatingBookingId]);

    // Filter out booked offices — hide any office that has an active, non-frozen booking
    const availableOffices = useMemo(() => {
        let opOffices = filteredOffices.filter(office => office.status === OPERATIONAL_STATUSES.OPERATIONAL);
        
        if (showAvailableOfficesOnly) {
            opOffices = opOffices.filter(office => {
                // Always show the currently selected office
                if (formData.office === office._id) return true;
                const blockingBookings = (office.bookings || []).filter(isActiveNonFrozenBooking);
                return blockingBookings.length === 0;
            });
        }
        return opOffices;
    }, [filteredOffices, formData.office, isActiveNonFrozenBooking, showAvailableOfficesOnly]);

    // Filter out full-time-booked seats — hide any seat with an active, non-frozen full-time booking
    const availableSeats = useMemo(() => {
        let opSeats = filteredSeats.filter(seat => seat.status === OPERATIONAL_STATUSES.OPERATIONAL);
        
        if (showAvailableOnly) {
            opSeats = opSeats.filter(seat => {
                // Always show the currently selected seat
                if (formData.seat === seat._id) return true;
                const blockingBookings = (seat.bookings || []).filter(isActiveNonFrozenBooking);
                
                // If the seat has ANY active full-time booking, it's not available in the filtered view
                const hasFullTimeBooking = blockingBookings.some(b => isBookingFullTime(b));
                return !hasFullTimeBooking;
            });
        }
        return opSeats;
    }, [filteredSeats, formData.seat, isActiveNonFrozenBooking, showAvailableOnly]);

    // Compute inline time conflict warning for the selected part-time seat
    // Uses date range for precision — warn only when the new booking's dates actually overlap
    // Helper: converts "HH:mm" string to total minutes since midnight for numeric comparison
    const toMinutes = (hhmm) => {
        if (!hhmm) return null;
        const [h, m] = hhmm.split(':').map(Number);
        return h * 60 + m;
    };

    // Helper: returns true if [aStart, aEnd) and [bStart, bEnd) overlap (in minutes since midnight)
    const timesOverlap = (aStart, aEnd, bStart, bEnd) => {
        if (aStart === null || aEnd === null || bStart === null || bEnd === null) return false;
        return aStart < bEnd && bStart < aEnd;
    };

    // Inline time conflict warning — no date dependency so it fires as soon as time is entered.
    // Checks ALL active non-frozen part-time bookings on the selected seat.
    const seatTimeConflictWarning = useMemo(() => {
        if (!formData.seat || isFullDayAccess || !formData.dailyStartTime || !formData.dailyEndTime) {
            return null;
        }

        const newStartMin = toMinutes(formData.dailyStartTime);
        const newEndMin = toMinutes(formData.dailyEndTime);
        if (newStartMin === null || newEndMin === null || newStartMin >= newEndMin) return null;

        const selectedSeat = seats?.find(s => s._id === formData.seat);
        if (!selectedSeat) return null;

        // Check all active non-frozen part-time bookings — no date range filter here
        // so the warning fires immediately without requiring startDate/endDate to be set.
        const partTimeBookings = (selectedSeat.bookings || []).filter(booking => {
            if (isUpdating && booking._id === updatingBookingId) return false;
            const isStatusAllowed = !booking?.status || ACTIVE_BOOKING_STATUSES.includes(booking?.status);
            if (!isStatusAllowed) return false;
            if (booking.freeze?.isFrozen) return false;
            return !isBookingFullTime(booking);
        });

        if (partTimeBookings.length === 0) return null;

        const timeConflicts = partTimeBookings.filter((existing) => {
            const existStartMin = toMinutes(moment(existing.dailyStartTime).format('HH:mm'));
            const existEndMin = toMinutes(moment(existing.dailyEndTime).format('HH:mm'));
            return timesOverlap(newStartMin, newEndMin, existStartMin, existEndMin);
        });

        if (timeConflicts.length === 0) return null;

        return `Time slot (${formData.dailyStartTime} – ${formData.dailyEndTime}) overlaps with existing booking(s) by: ${getUsersListText(timeConflicts)}.`;
    }, [formData.seat, formData.dailyStartTime, formData.dailyEndTime, isFullDayAccess, seats, isUpdating, updatingBookingId]);

    return (
        <ModalX
            isOpen={isOpen}
            onClose={onClose}
            title={isUpdating ? "Edit Booking" : "Add Booking"}
            maxWidth="xl"
        >
            <ConfirmationDialog />
            <form onSubmit={handleFormSubmit}>
                <div className="space-y-4">
                    {/* Booking Info Display */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Input
                                    name='startDate'
                                    type="date"
                                    label="Start Date"
                                    value={formData.startDate}
                                    onChange={(e) => handleInputChange(e)}
                                // min={moment().format('YYYY-MM-DD')} // TODO: add this functionality by passing the contract start,end dates
                                // max={moment().add(1, 'year').format('YYYY-MM-DD')}
                                />
                            </div>
                            <div>
                                <Checkbox
                                    checked={isOpenEnded}
                                    onChange={(e) => setIsOpenEnded(e.target.checked)}
                                    label={
                                        <>
                                            Open-ended
                                            <p className="text-xs text-gray-600">
                                                If checked, the booking will have no end date
                                            </p>
                                        </>
                                    }
                                    size="sm"
                                />

                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Status</label>
                                <div className="flex">{InterpretActiveStatus(formData.status)}</div>
                            </div>
                            <div className="mt-2">
                                {
                                    !isOpenEnded && (
                                        <Input
                                            name='endDate'
                                            type="date"
                                            label="End Date"
                                            value={formData.endDate}
                                            disabled={isOpenEnded}
                                            onChange={(e) => handleInputChange(e)}
                                        />
                                    )
                                }
                            </div>
                        </div>
                    </div>

                    <Checkbox
                        checked={isFullDayAccess}
                        onChange={(e) => setIsFullDayAccess(e.target.checked)}
                        label="Full day access"
                        size="sm"
                    />
                    {
                        !isFullDayAccess && (
                            <div className="space-y-2">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Input
                                        type="time"
                                        label="Daily Start Time"
                                        value={formData.dailyStartTime}
                                        onChange={(e) => handleTimeChange('dailyStartTime', e.target.value)}
                                        disabled={isFullDayAccess}
                                        error={hasSubmitted && formData.seat && !formData.dailyStartTime}
                                    />
                                    <Input
                                        type="time"
                                        label="Daily End Time"
                                        value={formData.dailyEndTime}
                                        onChange={(e) => handleTimeChange('dailyEndTime', e.target.value)}
                                        disabled={isFullDayAccess}
                                        error={hasSubmitted && formData.seat && !formData.dailyEndTime}
                                    />
                                </div>
                                {hasSubmitted && formData.seat && (!formData.dailyStartTime || !formData.dailyEndTime) && (
                                    <div className="text-xs text-red-600">
                                        ⚠ Both start and end times are required for part-time seat access.
                                    </div>
                                )}
                            </div>
                        )
                    }

                    {/* Tabs for Office/Seat */}
                    <Tabs value={activeTab} className="w-full">
                        <TabsHeader className="rounded-lg bg-gray-100 p-2">
                            {
                                isUpdating ?
                                    (
                                        <>
                                            {
                                                formData?.office && <Tab
                                                    value="office"
                                                    className="flex items-center gap-2"
                                                    onClick={() => setActiveTab('office')}
                                                >
                                                    <ScanQrCodeIcon size={16} />
                                                    Office
                                                </Tab>
                                            }

                                            {
                                                formData?.seat &&
                                                <Tab
                                                    value="seat"
                                                    className="flex items-center gap-2"
                                                    onClick={() => setActiveTab('seat')}
                                                >
                                                    <SofaIcon size={16} />
                                                    Seat
                                                </Tab>
                                            }

                                        </>
                                    ) : (
                                        <>
                                            <Tab
                                                value="office"
                                                className="flex items-center gap-2"
                                                onClick={() => setActiveTab('office')}
                                            >
                                                <ScanQrCodeIcon size={16} />
                                                Office
                                            </Tab>
                                            <Tab
                                                value="seat"
                                                className="flex items-center gap-2"
                                                onClick={() => setActiveTab('seat')}
                                            >
                                                <SofaIcon size={16} />
                                                Seat
                                            </Tab>
                                        </>
                                    )
                            }
                        </TabsHeader>

                        <TabsBody>

                            <TabPanel value="office" className="p-0">
                                <div className="mt-4">
                                    <Input
                                        label="Search Offices"
                                        placeholder="Search by office code"
                                        icon={<SearchIcon className="h-4 w-4" />}
                                        value={searchTerm}
                                        disabled={isLoadingOffices}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <div className="mt-2 flex items-center justify-end">
                                        <Checkbox
                                            checked={showAvailableOfficesOnly}
                                            onChange={(e) => setShowAvailableOfficesOnly(e.target.checked)}
                                            label={<span className="text-sm text-gray-700">Show available offices only</span>}
                                            size="sm"
                                        />
                                    </div>
                                    <div className="h-[250px] overflow-y-auto mt-4">
                                        {isLoadingOffices ? (
                                            <div className="flex justify-center items-center h-full">
                                                <Spinner className="w-4 h-4" />
                                            </div>
                                        ) : (
                                            <List>
                                                {availableOffices
                                                    .slice() // Create a copy to avoid mutating the original array
                                                    .map((office) => {
                                                        const activeBookings = (office.bookings || []).filter(isActiveNonFrozenBooking);
                                                        const isBooked = activeBookings.length > 0;
                                                        return (
                                                            <React.Fragment key={office._id}>
                                                                <ListItem
                                                                    className="py-1"
                                                                    selected={formData.office === office._id}
                                                                    onClick={() => handleOfficeSelection(office._id)}
                                                                >
                                                                    <div className="flex w-full items-center">
                                                                        <Checkbox
                                                                            checked={formData.office === office._id}
                                                                            className="h-4 w-4"
                                                                            ripple={false}
                                                                            readOnly
                                                                        />
                                                                        <div className="flex flex-col w-full ml-3">
                                                                            <div className="font-medium">
                                                                                Office # {office.number} ({office.code})
                                                                            </div>
                                                                            <div className="text-xs text-gray-600">
                                                                                {office.capacity} Seater | Rate: {office.monthlyRate}/month
                                                                                {isBooked && (
                                                                                    <span className="ml-2 font-medium text-red-500">
                                                                                        · Booked
                                                                                    </span>
                                                                                )}
                                                                                <br />
                                                                                {getOrdinal(office.floor.number)} Floor, {office.floor.building.name}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </ListItem>
                                                                {/* Expanded info panel for selected office */}
                                                                {formData.office === office._id && (
                                                                    <div className="border-l-2 border-gray-300 ml-4 pl-3 pb-2">
                                                                        {/* Active bookings on this office */}
                                                                        {activeBookings.length > 0 && (
                                                                            <div className="mt-1 mb-2">
                                                                                <div className="text-xs font-semibold text-gray-600 mb-1">Active Bookings:</div>
                                                                                {activeBookings.slice(0, 5).map((booking, idx) => (
                                                                                    <div key={idx} className="flex justify-between items-center text-xs text-gray-500 py-0.5">
                                                                                        <div className="flex flex-col">
                                                                                            <span>{moment(booking.startDate).format('DD MMM YYYY')} - {booking.endDate ? moment(booking.endDate).format('DD MMM YYYY') : 'Onwards'}</span>
                                                                                            <span className="text-[10px] text-gray-400">
                                                                                                {isBookingFullTime(booking) 
                                                                                                    ? 'Full Day' 
                                                                                                    : `${moment(booking.dailyStartTime).format('hh:mm a')} – ${moment(booking.dailyEndTime).format('hh:mm a')}`}
                                                                                            </span>
                                                                                        </div>
                                                                                        <Link
                                                                                            to={`/customer/${booking?.contract?.user?._id}`}
                                                                                            className="flex items-center gap-1 text-blue-600 hover:underline"
                                                                                            target="_blank"
                                                                                            onClick={e => e.stopPropagation()}
                                                                                        >
                                                                                            <ExternalLinkIcon className="w-3 h-3" />
                                                                                            {booking?.contract?.user?.name || 'Deleted User'}
                                                                                        </Link>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                            </List>
                                        )}
                                    </div>
                                </div>
                            </TabPanel>
                            <TabPanel value="seat" className="p-0">
                                <div className="mt-4">
                                    <Input
                                        label="Search Seats"
                                        placeholder="Search by seat code"
                                        icon={<SearchIcon className="h-4 w-4" />}
                                        value={searchTerm}
                                        disabled={isLoadingSeats}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <div className="mt-2 flex items-center justify-end">
                                        <Checkbox
                                            checked={showAvailableOnly}
                                            onChange={(e) => setShowAvailableOnly(e.target.checked)}
                                            label={<span className="text-sm text-gray-700">Show available seats only</span>}
                                            size="sm"
                                        />
                                    </div>
                                    <div className="h-[250px] overflow-y-auto mt-4">
                                        {isLoadingSeats ? (
                                            <div className="flex justify-center items-center h-full">
                                                <Spinner className="w-4 h-4" />
                                            </div>
                                        ) : (
                                            <List>
                                                {availableSeats
                                                    .slice()
                                                    .map((seat) => {
                                                        const isSelected = formData.seat === seat._id;
                                                        const activeBookings = (seat.bookings || []).filter(isActiveNonFrozenBooking);
                                                        return (
                                                            <React.Fragment key={seat._id}>
                                                                <ListItem
                                                                    className="py-1"
                                                                    selected={isSelected}
                                                                    onClick={() => handleSeatSelection(seat._id)}
                                                                >
                                                                    <div className="flex w-full items-center">
                                                                        <Checkbox
                                                                            checked={isSelected}
                                                                            className="h-4 w-4"
                                                                            ripple={false}
                                                                            readOnly
                                                                        />
                                                                        <div className="flex flex-col w-full ml-3">
                                                                            <div className="font-medium">
                                                                                Seat # {seat.seatCode}
                                                                                {activeBookings.length > 0 && (() => {
                                                                                    const hasFull = activeBookings.some(b => isBookingFullTime(b));
                                                                                    const hasPart = activeBookings.some(b => !isBookingFullTime(b));
                                                                                    let badgeText = `${activeBookings.length} active booking(s)`;
                                                                                    let badgeColor = "text-amber-600";
                                                                                    if (hasFull && hasPart) {
                                                                                        badgeText = "Full & Part-time booked";
                                                                                        badgeColor = "text-red-500";
                                                                                    } else if (hasFull) {
                                                                                        badgeText = "Full-time booked";
                                                                                        badgeColor = "text-red-500";
                                                                                    } else {
                                                                                        badgeText = `${activeBookings.length} part-time booking(s)`;
                                                                                        badgeColor = "text-amber-600";
                                                                                    }
                                                                                    return (
                                                                                        <span className={`ml-2 font-medium text-xs ${badgeColor}`}>
                                                                                            · {badgeText}
                                                                                        </span>
                                                                                    );
                                                                                })()}
                                                                            </div>
                                                                            <div className="text-xs text-gray-600">
                                                                                Space {seat.coworking.number} ({seat.coworking.code})
                                                                                <br />
                                                                                {seat.seatType} - {seat.deskType} Desk
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </ListItem>
                                                                {/* Expanded info panel for selected seat */}
                                                                {isSelected && (
                                                                    <div className="border-l-2 border-gray-300 ml-4 pl-3 pb-2">
                                                                        {/* Active bookings on this seat */}
                                                                        {activeBookings.length > 0 && (
                                                                            <div className="mt-1 mb-2">
                                                                                <div className="text-xs font-semibold text-gray-600 mb-1">Active Bookings:</div>
                                                                                {activeBookings.slice(0, 5).map((booking, idx) => (
                                                                                    <div key={idx} className="flex justify-between items-center text-xs text-gray-500 py-0.5">
                                                                                        <div className="flex flex-col">
                                                                                            <span>{moment(booking.startDate).format('DD MMM YYYY')} - {booking.endDate ? moment(booking.endDate).format('DD MMM YYYY') : 'Onwards'}</span>
                                                                                            <span className="text-[10px] text-gray-400">
                                                                                                {isBookingFullTime(booking) 
                                                                                                    ? 'Full Day' 
                                                                                                    : `${moment(booking.dailyStartTime).format('hh:mm a')} – ${moment(booking.dailyEndTime).format('hh:mm a')}`}
                                                                                            </span>
                                                                                        </div>
                                                                                        <Link
                                                                                            to={`/customer/${booking?.contract?.user?._id}`}
                                                                                            className="flex items-center gap-1 text-blue-600 hover:underline"
                                                                                            target="_blank"
                                                                                            onClick={e => e.stopPropagation()}
                                                                                        >
                                                                                            <ExternalLinkIcon className="w-3 h-3" />
                                                                                            {booking?.contract?.user?.name || 'Deleted User'}
                                                                                        </Link>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                        {/* Inline time conflict warning */}
                                                                        {seatTimeConflictWarning && (
                                                                            <div className="border-l-4 border-red-500 bg-red-50 p-2 rounded text-xs text-red-700">
                                                                                ⚠ Time conflict: {seatTimeConflictWarning}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                            </List>
                                        )}
                                    </div>
                                </div>
                            </TabPanel>
                        </TabsBody>
                    </Tabs>


                    {/* Rent Information */}
                    <div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Input
                                type="number"
                                label="Total Rent *"
                                name="rentTotal"
                                value={formData.rentTotal}
                                onChange={handleInputChange}
                                error={hasSubmitted && (!formData.rentTotal || Number(formData.rentTotal) < 1000)}
                            />
                            <Input
                                type="number"
                                label="Advance"
                                name="advanceAmount"
                                value={formData.advanceAmount}
                                onChange={handleInputChange}
                            />
                            <Input
                                type="number"
                                label="Security"
                                name="securityAmount"
                                value={formData.securityAmount}
                                onChange={handleInputChange}
                            />
                        </div>
                        {showFirstMonthRent && (
                            <div className="mt-4">
                                <Input
                                    type="number"
                                    label="First Month Rent (PKR)"
                                    name="firstMonthRent"
                                    placeholder="Leave blank to use standard rent"
                                    value={formData.firstMonthRent}
                                    onChange={handleFirstMonthRentChange}
                                    onFocus={handleFirstMonthRentFocus}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Enter a prorated amount if this booking starts mid-month.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Comment */}
                    <Textarea
                        label="Comments"
                        name="comment"
                        value={formData.comment}
                        onChange={handleInputChange}
                    />

                    {/* Submit Button */}
                    <div className="flex justify-end gap-4">
                        <Button
                            variant="text"
                            onClick={onClose}
                            className="rounded-full"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="rounded-full"
                            disabled={updateBookingMutation.isLoading || addBookingMutation.isLoading}
                        >
                            {updateBookingMutation.isLoading || addBookingMutation.isLoading ? (
                                <div className="flex items-center gap-2">
                                    <Spinner className="w-4 h-4" />
                                    Updating...
                                </div>
                            ) : (
                                isUpdating ? 'Update Booking' : 'Add Booking'
                            )}
                        </Button>
                    </div>
                </div>
            </form>
        </ModalX>
    );
};

export default BookingFormModal;